import router from '@/router';
import theme from '@/factories/theme';
import localStore from 'store';
import vuexStore from '@/store';
import EventBus from '@/factories/eventBus';
import { inIframe, embedApiEnabled } from '@/globals';
import { createBridge } from '../../packages/frontend-sdk/src/bridge/bridge';

/**
 * Host <-> iframe bridge adapter.
 *
 * Child side (this app running inside an iframe): one shared cy-bridge to
 * window.parent. Every `connect({ methods })` call registers more methods on
 * it, so the many call sites (App, LessonApi, lesson lists...) share a single
 * handshake. Legacy Penpal 3 parents (old SCORM exports) are still answered.
 *
 * Parent side (this app framing the portal): `connectToChild()` creates one
 * bridge per iframe.
 */

let childBridge = null;

// A promise that never settles, for call sites that call connect() outside an
// iframe and just chain `.then` (matches Penpal's behaviour without a timeout).
const NEVER = new Promise(() => {});

const defaultChildMethods = () => ({
  route: (path) => router.push('/' + path),

  setTheme: (config) => theme.setCustomTheme(config),

  setAuth: (auth) => {
    if (auth.testing && auth.id) {
      localStore.set(`auth_${auth.id}`, { id: auth.id, token: auth.token });
      localStore.set('portal_testing_pid', auth.id);
    } else {
      localStore.remove('portal_testing_pid');
      localStore.set('portal_auth', auth);
    }
    EventBus.$emit('portalAuthSet', auth);
  },

  setCSRF: (csrfToken) => {
    localStore.set('csrf_token', csrfToken);
  },

  initScormCourse: (params) => {
    const { app_key, token } = params;
    if (app_key && token) {
      localStore.set('conveyour_appkey', app_key);
      localStore.set('conveyour_token', token);
    }

    vuexStore.dispatch('loginScormLearner', params);
  },

  runReport: () => {
    EventBus.$emit('runReport');
  },

  getRoute: () => router.currentRoute.fullPath,
});

/**
 * The shared child-side bridge to window.parent (null outside an iframe).
 * The parent origin is locked onto during the handshake: whoever is allowed
 * to frame us (Content-Security-Policy frame-ancestors) is our peer.
 */
export const getChildBridge = () => {
  if (!inIframe) return null;

  if (!childBridge) {
    childBridge = createBridge({
      remote: window.parent,
      origin: '*',
      parent: false,
      legacy: true,
      methods: defaultChildMethods(),
    });
  }

  return childBridge;
};

// Events raised before the handshake completes are replayed once it does, so
// lesson:complete etc. are never lost to a slow host.
let queuedEvents = [];
let flushArmed = false;

/**
 * Fire-and-forget event to the host page (no-op outside an iframe).
 * Contract v1 events: ready, navigate, resize, overlay, auth,
 * lesson:ready, lesson:complete, lesson:failed.
 */
export const emitToParent = (event, payload) => {
  const bridge = getChildBridge();
  if (!bridge) return false;

  if (bridge.connected) {
    bridge.emit(event, payload);
    return true;
  }

  queuedEvents.push([event, payload]);
  if (!flushArmed) {
    flushArmed = true;
    bridge.promise.then(
      () => {
        const events = queuedEvents;
        queuedEvents = [];
        flushArmed = false;
        events.forEach(([e, p]) => bridge.emit(e, p));
      },
      () => {
        queuedEvents = [];
        flushArmed = false;
      }
    );
  }
  return false;
};

export const connect = (options = {}) => {
  const bridge = getChildBridge();

  if (!bridge) {
    return { promise: NEVER, destroy: () => {} };
  }

  if (options.methods) {
    bridge.addMethods(options.methods);
  }

  // destroy is intentionally a no-op: the bridge is shared by the whole app
  return { promise: bridge.promise, destroy: () => {}, bridge };
};

/**
 * Frame a child page and connect to it.
 *
 * @param {object} options
 * @param {string} [options.url]         set as iframe.src
 * @param {HTMLIFrameElement} [options.iframe]
 * @param {Element} [options.appendTo]
 * @param {Record<string, Function>} [options.methods] exposed to the child
 * @param {string} [options.childOrigin] defaults to the origin of `url`
 * @param {number} [options.timeout]     ms, 0 = wait forever
 */
export const connectToChild = (options = {}) => {
  const iframe = options.iframe || document.createElement('iframe');

  if (options.url) {
    iframe.src = options.url;
  }

  if (options.appendTo && !iframe.parentNode) {
    options.appendTo.appendChild(iframe);
  }

  if (!iframe.contentWindow) {
    throw new Error('connectToChild: iframe must be in the document');
  }

  const origin =
    options.childOrigin || new URL(iframe.src, window.location.href).origin;

  const bridge = createBridge({
    remote: iframe.contentWindow,
    origin,
    parent: true,
    methods: options.methods || {},
    timeout: options.timeout || 0,
  });

  return {
    promise: bridge.promise,
    destroy: bridge.destroy,
    iframe,
    bridge,
  };
};

export const bindMethods = (_this, methods = []) => {
  let bound = {};
  methods.forEach((m) => {
    bound[m] = _this[m].bind(_this);
  });
  return bound;
};

export const onParent = (method, cb, fallback) => {
  if (!inIframe) {
    if (fallback) {
      return fallback();
    }
    return false;
  }

  connect().promise.then((parent) => {
    if (!parent || !parent[method]) return;
    cb(parent[method].bind(parent));
  });
};

export const parentNavigate = (path, trigger = false, replace = true) => {
  return onParent(
    'navigate',
    (navigate) => {
      navigate(path, trigger, replace);
    },
    () => {
      router.push(path);
    }
  );
};

export const parentTrack = (event = 'ui2-event', properties = {}) => {
  return onParent('track', (track) => {
    track(event, properties);
  });
};

export class InitialConnection {
  resolvedIn = 0;

  constructor(options = {}) {
    this.options = options;
  }

  init() {
    this.connection = connect(this.options);

    this.then(() => {
      this.resolved = true;
      emitToParent('ready', { route: router.currentRoute.fullPath });
    });

    this.publishRoutes();

    return this;
  }

  timeout(cb, time = 1000) {
    if (!cb) return false;
    setTimeout(() => {
      if (!this.resolved) {
        cb.call(this);
      }
    }, time);
  }

  promise() {
    return this.connection.promise;
  }

  then(cb) {
    return this.promise().then(cb);
  }

  catch(cb) {
    return this.promise().catch(cb);
  }

  publishRoutes() {
    router.afterEach((to) => {
      let path = to.path;
      if (to.query && Object.keys(to.query).length) {
        path += '?' + new URLSearchParams(to.query).toString();
      }

      emitToParent('navigate', { path, name: to.name });

      this.then((parent) => {
        if (!parent || !parent.navigate) return;
        parent.navigate(path);
      });
    });
  }
}

export const initParentConnection = () => {
  if (embedApiEnabled()) {
    let parentConnection = new InitialConnection();
    parentConnection.init();
  }
};
